import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { GroupTypes } from '@carm/_core/access-control-mgt/models/acl.model';
import { AuthRouteGuard } from '@carm/_core/guards/auth-route.guard';
import { NotFoundComponent } from '@carm/features/private/not-found/not-found.component';
import { CarmResources } from './_core/access-control-mgt/models';
import { DashboardHomePageComponent } from './features/private/dashboard/pages/dashboard-home-page/dashboard-home-page.component';
import { HomePageComponent } from '@carm/features/public/home/pages/home-page/home-page.component';
import { CustomsBrokersListComponent } from './features/public/customs-brokers/components/customs-brokers-list.component';

const routes: Routes = [
  {
    path: 'cbsa-homepage',
    component: HomePageComponent,
    resolve: {
      url: 'externalUrlRedirectResolver',
    },
    data: {
      externalUrl: 'https://www.cbsa-asfc.gc.ca/menu-eng.html',
    },
  },
  {
    path: 'homepage',
    loadChildren: () =>
      import('./features/public/home/home.module').then(mod => mod.HomeModule),
  },
  {
    path: 'alert-details',
    loadChildren: () =>
      import('./features/public/alert-details/alert-details.module').then(
        mod => mod.AlertDetailsModule
      ),
  },
  {
    path: 'national-rulings',
    loadChildren: () =>
      import('./features/public/national-rulings/national-rulings.module').then(
        mod => mod.NationalRulingsModule
      ),
  },
  {
    path: 'public-bulletins',
    loadChildren: () =>
      import(
        './features/public/public-bulletins/public-bulletins.module'
      ).then(mod => mod.PublicBulletinsModule),
  },
  {
    path: 'faq',
    loadChildren: () =>
      import('./features/public/faq/faq.module').then(mod => mod.FaqModule),
  },
  {
    path: 'customs-brokers',
    component: CustomsBrokersListComponent,
    pathMatch: 'full'
  },
  {
    path: 'carm-api',
    loadChildren: () =>
      import('./features/public/carm-api/carm-api.module').then(
        mod => mod.CarmApiModule
      ),
  },
  {
    path: 'onboarding-documentation',
    loadChildren: () =>
      import(
        './features/public/onboarding-documentation/onboarding-documentation.module'
      ).then(mod => mod.OnboardingDocumentationModule),
  },
  {
    path: 'heartbeat',
    loadChildren: () =>
      import('./features/public/heartbeat/heartbeat.module').then(
        mod => mod.HeartbeatModule
      ),
  },
  // *************************
  // AuthRouting
  // TO-DO add route guards
  // *************************
  {
    path: 'auth',
    loadChildren: () =>
      import('./_auth/user-mgt/user-mgt.module').then(mod => mod.UserMgtModule),
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/access-mgt/manage-business',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import(
        './features/private/setup-my-portal/business-access-mgt/business-access-mgt.module'
      ).then(mod => mod.BusinessAccessMgtModule),
    data: {
      acl: [
        {
          resource: CarmResources.DOA,
          group: GroupTypes.Reader,
          isNativeAcl: true,
        },
        {
          resource: CarmResources.MANAGE_RELATIONSHIPS,
          group: GroupTypes.Reader,
          isNativeAcl: true,
        },
      ],
    },
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/access-mgt/manage',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import(
        './features/private/setup-my-portal/employees-access-mgt/employees-access-mgt.module'
      ).then(mod => mod.EmployeesAccessMgtModule),
    data: {
      acl: [
        {
          resource: CarmResources.EMPLOYEE,
          group: GroupTypes.Reader,
          isNativeAcl: true,
        },
      ],
    },
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/access-mgt/manage/access-request-reject',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import(
        './features/private/setup-my-portal/employees-access-mgt/employees-access-mgt.module'
      ).then(mod => mod.EmployeesAccessMgtModule),
    data: {
      acl: [
        {
          resource: CarmResources.EMPLOYEE,
          group: GroupTypes.Reader,
          isNativeAcl: true,
        },
      ],
    },
  },
  // *************************
  // Private Routing
  // TO-DO add route guards
  // *************************
  {
    path: 'private/nb/:nativeBn9/p/:bn15/accounts-and-profiles',
    loadChildren: () =>
      import('./features/private/accounts-and-profiles/accounts-and-profiles.module').then(mod => mod.AccountsAndProfilesModule),
        data: {
          acl: [
            {
              resource: CarmResources.PROGRAM,
              group: GroupTypes.Reader,
            },
          ],
        },
  },
  {
    path: 'private/accounts-and-profiles', // orphans
    loadChildren: () =>
      import('./features/private/accounts-and-profiles/accounts-and-profiles.module').then(mod => mod.AccountsAndProfilesModule),
        data: {
          acl: [
            {
              resource: CarmResources.PROGRAM,
              group: GroupTypes.Reader,
              isNativeAcl: true,
            },
          ],
        },
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/access-mgt/view-api-key',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import('./features/private/setup-my-portal/view-api-key/view-api-key.module').then(
        mod => mod.ViewApiKeyModule
      ),
    data: {
      acl: [
        {
          resource: CarmResources.API,
          group: GroupTypes.Reader,
          isNativeAcl: true,
        },
      ],
    },
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/access-mgt/cbsa-enrolment-mgt',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import('./features/private/setup-my-portal/cbsa-enrolment-mgt/cbsa-enrolment-mgt.module').then(
        mod => mod.CbsaEnrolmentMgtModule
      ),
    data: {
      acl: [
        {
          resource: CarmResources.PROGRAM_ENROLMENT,
          group: GroupTypes.Reader,
          isNativeAcl: true,
        },
      ],
    },
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/access-mgt/manage/client-groups',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import('./features/private/setup-my-portal/client-group-mgt/client-group.module').then(
        mod => mod.ClientGroupModule
      ),
    data: {
      acl: [
        {
          resource: CarmResources.CLIENT_GROUPS,
          group: GroupTypes.Reader,
          isNativeAcl: true
        },
      ],
    },
  },
  {
    path: 'private/accounts-and-profiles/personal-profile',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import('./features/private/accounts-and-profiles/features/user-profile/user.module').then(
        mod => mod.UserModule
      ),
    data: {
      acl: [
        {
          resource: CarmResources.PERSONAL_PROFILE,
          group: GroupTypes.Reader,
        },
      ],
    },
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/accounts-and-profiles/personal-profile',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import('./features/private/accounts-and-profiles/features/user-profile/user.module').then(
        mod => mod.UserModule
      ),
    data: {
      acl: [
        {
          resource: CarmResources.PERSONAL_PROFILE,
          group: GroupTypes.Reader,
        },
      ],
    },
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/accounts-and-profiles/link-business',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import(
        './features/private/accounts-and-profiles/features/link-business/link-business.module'
      ).then(mod => mod.LinkBusinessModule),
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/registration/register-business',
    loadChildren: () =>
      import('./features/private/business-registration/business-registration.module').then(mod => mod.BusinessRegistrationModule),
  },
  {
    path: 'private/registration/register-business',
    loadChildren: () =>
      import('./features/private/business-registration/business-registration.module').then(mod => mod.BusinessRegistrationModule),
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/financial-information',
    canActivate: [AuthRouteGuard],
    loadChildren: () => import('./features/private/financial/financial.module').then(mod => mod.FinancialModule),
    data: {
      acl: [
        {
          resource: CarmResources.FINANCIAL_STATEMENTS,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.BROKER_SUMMARY,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.INTEREST_SIMULATION,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.TRANSACTION_HISTORY,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.CLIENT_TRANSACTION_HISTORY,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.BONDS_DASHBOARD,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.BONDS_LIST,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SURETY_PROVIDER,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.COLLECTIONS,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SOA_INTRA_PROGRAM,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SOA_PROGRAM,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SOA_BUSINESS,
          group: GroupTypes.Reader,
        }
      ]
    }
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/program-enrolment',
    canActivate: [AuthRouteGuard],
    loadChildren: () => import('./features/private/financial/financial.module').then(mod => mod.FinancialModule),
    data: {
      acl: [
        {
          resource: CarmResources.FINANCIAL_STATEMENTS,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.BROKER_SUMMARY,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.INTEREST_SIMULATION,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.TRANSACTION_HISTORY,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.CLIENT_TRANSACTION_HISTORY,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.BONDS_DASHBOARD,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.BONDS_LIST,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SURETY_PROVIDER,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.COLLECTIONS,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SOA_INTRA_PROGRAM,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SOA_PROGRAM,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SOA_BUSINESS,
          group: GroupTypes.Reader,
        }
      ]
    }
  },
  {
    path: 'private/program-enrolment',
    canActivate: [AuthRouteGuard],
    loadChildren: () => import('./features/private/financial/financial.module').then(mod => mod.FinancialModule),
    data: {
      acl: [
        {
          resource: CarmResources.FINANCIAL_STATEMENTS,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.BROKER_SUMMARY,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.INTEREST_SIMULATION,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.TRANSACTION_HISTORY,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.CLIENT_TRANSACTION_HISTORY,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.BONDS_DASHBOARD,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.BONDS_LIST,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SURETY_PROVIDER,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.COLLECTIONS,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SOA_INTRA_PROGRAM,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SOA_PROGRAM,
          group: GroupTypes.Reader,
        },
        {
          resource: CarmResources.SOA_BUSINESS,
          group: GroupTypes.Reader,
        }
      ]
    }
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/moneris',
    canActivate: [AuthRouteGuard],
    loadChildren: () => import('./features/private/moneris/moneris.module').then(mod => mod.MonerisModule),
    data: {
      acl: [
        { resource: CarmResources.MAKE_PAYMENT, group: GroupTypes.Editor },
      ],
    },
  },
  {
    path: 'private/moneris',
    canActivate: [AuthRouteGuard],
    loadChildren: () => import('./features/private/moneris/moneris.module').then(mod => mod.MonerisModule),
    data: {
      acl: [
        { resource: CarmResources.MAKE_PAYMENT, group: GroupTypes.Editor },
      ],
    },
  },
  {
    path: 'moneris/callback', // do not change. configured by moneris
    canActivate: [AuthRouteGuard],
    loadChildren: () => import('./features/private/moneris/moneris.module').then(mod => mod.MonerisModule),
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/payments',
    canActivate: [AuthRouteGuard],
    loadChildren: () => import('./features/private/payments/payments.module').then(mod => mod.PaymentsModule),
    data: {
      acl: [
        { resource: CarmResources.PAYMENTS, group: GroupTypes.Reader },
        { resource: CarmResources.APPLY_CREDITS, group: GroupTypes.Reader },
      ],
    },
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/rulings',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import('./features/private/rulings_appeals_compliance/features/rulings/rulings.module').then(mod => mod.RulingsModule),
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/dashboard',
    canActivate: [AuthRouteGuard],
    component: DashboardHomePageComponent
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/declarations',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import('./features/private/declarations/declarations.module').then(
        mod => mod.DeclarationsModule
      ),
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/upload-documents',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import('./features/private/upload-documents/upload-documents.module').then(
        mod => mod.UploadDocumentsModule
      ),
    data: {
      acl: [
        {
          resource: CarmResources.DOCUMENTS,
          group: GroupTypes.Editor,
          isNativeAcl: true,
        },
      ],
    },
  },
  {
    path: 'private/nb/:nativeBn9/p/:bn15/notifications',
    canActivate: [AuthRouteGuard],
    loadChildren: () =>
      import('./features/private/notifications/notifications.module').then(
        mod => mod.NotificationsModule
      ),
    data: {
      acl: [
        { resource: CarmResources.NOTIFICATIONS, group: GroupTypes.Reader, isNativeAcl: true },
      ],
    },
  },
  { path: '', redirectTo: '/homepage', pathMatch: 'full' },
  { path: '**', component: NotFoundComponent },
  {
    path: 'upload-documents',
    loadChildren: () =>
      import('./features/private/upload-documents/upload-documents.module').then(
        m => m.UploadDocumentsModule
      ),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled'
      //enableTracing: true
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
